// components/members/MembersCarousel.js
import React, { useRef } from 'react';
import { Spinner, Button } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import MemberCard from './MemberCard';
import useMemberData from '../../hooks/useMemberData';
import { MEMBER_TYPES } from '../../utils/membersConfig';
import '../../App.css';

// Optional: Import Capacitor modules only if available
let Haptics, ImpactStyle;
try {
  ({ Haptics, ImpactStyle } = require('@capacitor/haptics'));
} catch (error) {
  console.log('Capacitor haptics not available');
}

const handleHapticsClick = async (callback) => {
  if (Haptics && ImpactStyle) {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      // Haptics not supported
    }
  }
  callback();
  window.scrollTo(0, 0);
};

// Helper function to get defaults based on member type
const getDefaults = (memberType) => {
  switch (memberType) {
    case MEMBER_TYPES.SENATE:
      return {
        title: 'U.S. Senators',
        viewAllLink: '/us/senators',
        viewAllText: 'View All Senators',
      };
    case MEMBER_TYPES.PRESIDENT:
      return {
        title: 'U.S. Presidents',
        viewAllLink: '/us/presidents',
        viewAllText: 'View All Presidents',
      };
    case MEMBER_TYPES.VICE_PRESIDENT:
      return {
        title: 'U.S. Vice Presidents',
        viewAllLink: '/us/vicepresidents',
        viewAllText: 'View All Vice Presidents',
      };
    case MEMBER_TYPES.JUSTICE:
      return {
        title: 'Supreme Court Justices',
        viewAllLink: '/us/justices',
        viewAllText: 'View All Justices',
      };
    case MEMBER_TYPES.HOUSE:
    default:
      return {
        title: 'U.S. Representatives',
        viewAllLink: '/us/houseofreps',
        viewAllText: 'View All Representatives',
      };
  }
};

/**
 * MembersCarousel - Displays multiple members in a horizontal scrolling carousel
 * 
 * @param {string} memberType - MEMBER_TYPES.HOUSE, MEMBER_TYPES.SENATE, MEMBER_TYPES.PRESIDENT, or MEMBER_TYPES.JUSTICE
 * @param {number} limit - Number of members to display (default: 10)
 * @param {string} title - Carousel heading title
 * @param {string} viewAllLink - Link for "View All" button
 * @param {string} viewAllText - Text for "View All" button
 * @param {boolean} showHeading - Whether to show the heading (default: true)
 * @param {boolean} showViewAll - Whether to show "View All" button (default: true)
 */
const MembersCarousel = ({ 
  memberType = MEMBER_TYPES.HOUSE,
  limit = 10,
  title,
  viewAllLink,
  viewAllText,
  showHeading = true,
  showViewAll = true
}) => {
  const scrollAreaRef = useRef(null);
  const { data, loading, error, config } = useMemberData(memberType, limit);

  // Get default values based on member type
  const defaults = getDefaults(memberType);

  const displayTitle = title || defaults.title;
  const displayViewAllLink = viewAllLink || defaults.viewAllLink;
  const displayViewAllText = viewAllText || defaults.viewAllText;

  if (loading) {
    return (
      <div className="members-carousel-container">
        {showHeading && (
          <Link
            onClick={() => handleHapticsClick(() => {})}
            id="pageLink"
            to={displayViewAllLink}
            className="carouselGroupTitleLink"
          >
            <h5 className="carousel-heading">
              <div className="title-container">
                <span className="heading-text">{displayTitle}</span>
                <ChevronRight id='clickChevron' className="heading-chevron" />
              </div>
            </h5>
          </Link>
        )}
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="members-carousel-container">
        {showHeading && (
          <h5 className="carousel-heading">
            <div className="title-container">
              <span className="heading-text">{displayTitle}</span>
            </div>
          </h5>
        )}
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <p style={{ color: 'var(--text-primary)', fontSize: '14px' }}>Error loading members</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="members-carousel-container">
        {showHeading && (
          <h5 className="carousel-heading">
            <div className="title-container">
              <span className="heading-text">{displayTitle}</span>
            </div>
          </h5>
        )}
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <p style={{ color: 'var(--text-primary)', fontSize: '14px' }}>No members available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="members-carousel-container news-section">
      {showHeading && (
        <Link
          onClick={() => handleHapticsClick(() => {})}
          id="pageLink"
          to={displayViewAllLink}
          className="carouselGroupTitleLink"
        >
          <h5 className="carousel-heading">
            <div className="title-container">
              <span className="heading-text">{displayTitle}</span>
              <ChevronRight id='clickChevron' className="heading-chevron" />
            </div>
          </h5>
        </Link>
      )}

      <div className="scroll-container">
        <div className="gradient-left" />
        <div className="gradient-right" />
        <div 
          className="scroll-area"
          ref={scrollAreaRef}
        >
          <div className="card-container">
            {data.map((member, index) => (
              <div key={member.bio_guide_id || member.president_num || member.full_name || index} className="card-wrapper">
                <div className="card-content">
                  <MemberCard 
                    member={member} 
                    memberType={memberType}
                    variant="carousel"
                    containerId="repContainer"
                  />
                </div>
              </div>
            ))}

            {/* View All button */}
            {showViewAll && (
              <div className="card-wrapper view-all-card">
                <div id="viewFullPageButton">
                  <Link 
                    id='pageLink'
                    to={displayViewAllLink}
                    onClick={() => handleHapticsClick(() => {})}
                  >
                    <Button id="viewFullLegislativePageButtonMobile" size="lg">
                      {displayViewAllText}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .members-carousel-container {
          margin-bottom: 2rem;
        }

        .members-carousel-container .scroll-container {
          position: relative;
          height: 220px;
        }

        .members-carousel-container .gradient-left,
        .members-carousel-container .gradient-right {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 48px;
          z-index: 5;
          pointer-events: none;
        }

        .members-carousel-container .gradient-left {
          left: 0;
        }

        .members-carousel-container .gradient-right {
          right: 0;
        }

        .members-carousel-container .scroll-area {
          box-sizing: border-box;
          height: 220px;
          overflow-x: scroll;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 17px;
        }

        .members-carousel-container .scroll-area::-webkit-scrollbar {
          display: none;
        }

        .members-carousel-container .card-container {
          display: flex;
          padding-left: 10px;
          padding-right: 10px;
        
        }

        .members-carousel-container .card-wrapper {
          flex: 0 0 300px;
          padding-right: 1rem;
          scroll-snap-align: center;
          box-sizing: border-box;
        }

        .members-carousel-container .card-wrapper:first-child {
          padding-left: 1rem;
        }

        .members-carousel-container .card-content {
          height: 139px;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          overflow: hidden;
          border-radius: 18px;
        }

        .members-carousel-container .view-all-card {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Heading styles */
        .members-carousel-container .carousel-heading {
          position: relative;
          padding: 16px 30px 8px 20px;
          margin: 0;
          color: var(--text-primary);
          height: 50px;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        .members-carousel-container .title-container {
          display: inline-flex;
          align-items: center;
          max-width: 100%;
          min-width: 0;
        }

        .members-carousel-container .heading-text {
          font-family: var(--site-font);
          font-size: clamp(15px, 4vw, 18px);
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: calc(100% - 24px);
          margin-right: 4px;
        }

        .members-carousel-container .heading-chevron {
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          min-width: 16px;
          display: inline-block;
        }

        .members-carousel-container .carouselGroupTitleLink {
          text-decoration: none !important;
          color: inherit !important;
        }

        @media screen and (max-width: 768px) {
          .members-carousel-container .card-wrapper {
            flex: 0 0 280px;
          }
        }
      `}</style>
    </div>
  );
};

export default MembersCarousel;